import { Box, Text } from '@mantine/core';
import { motion, type Transition } from 'framer-motion';
import {
	type CSSProperties,
	type FC,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import { StepperSetupStepKey } from '@/stepper';
import {
	STEPPER_PROGRESS_COMPLETE_PERCENT,
	STEPPER_STEP_TRANSITION_PROGRESS_TIMEOUT_MS
} from '../../constants';
import './StepperStepTransitionProgressBar.css';

interface StepperStepTransitionProgressBarProps {
	stepKey: StepperSetupStepKey;
	websiteUrl: string;
}

type StepperStepTransitionProgressTextStyle = CSSProperties & {
	'--stepper-progress-message-characters': number;
	'--stepper-progress-message-duration': string;
};

const transition: Transition = {
	duration: STEPPER_STEP_TRANSITION_PROGRESS_TIMEOUT_MS / 1000,
	ease: 'linear'
};

const StepperStepTransitionProgressBar: FC<
	StepperStepTransitionProgressBarProps
> = ({ stepKey, websiteUrl }) => {
	const { localization } = useHostContext();
	const [activeMessageIndex, setActiveMessageIndex] = useState(0);
	const messages = useMemo(() => {
		if (stepKey === StepperSetupStepKey.ExtractWebsiteContent) {
			if (websiteUrl.trim()) {
				return [
					localization.translate(
						'creator',
						'stepperOnboarding.steps.transitionProgress.extractWebsiteContent.website.readingWebsite'
					),
					localization.translate(
						'creator',
						'stepperOnboarding.steps.transitionProgress.extractWebsiteContent.website.gettingToKnowBusiness'
					),
					localization.translate(
						'creator',
						'stepperOnboarding.steps.transitionProgress.extractWebsiteContent.website.learningProducts'
					),
					localization.translate(
						'creator',
						'stepperOnboarding.steps.transitionProgress.extractWebsiteContent.website.understandingBusinessDetails'
					)
				];
			}

			return [
				localization.translate(
					'creator',
					'stepperOnboarding.steps.transitionProgress.extractWebsiteContent.noWebsite.analyzingBusinessInfo'
				),
				localization.translate(
					'creator',
					'stepperOnboarding.steps.transitionProgress.extractWebsiteContent.noWebsite.configuringAiAgent'
				)
			];
		}

		if (stepKey === StepperSetupStepKey.CustomizeGreeting) {
			return [
				localization.translate(
					'creator',
					'stepperOnboarding.steps.transitionProgress.customizeGreeting.configuringWorkflows'
				),
				localization.translate(
					'creator',
					'stepperOnboarding.steps.transitionProgress.customizeGreeting.pretrainingAgent'
				),
				localization.translate(
					'creator',
					'stepperOnboarding.steps.transitionProgress.customizeGreeting.runningTests'
				),
				localization.translate(
					'creator',
					'stepperOnboarding.steps.transitionProgress.customizeGreeting.talkingSoon'
				)
			];
		}

		return [
			localization.translate(
				'creator',
				'stepperOnboarding.steps.transitionProgress.callSummariesTransfers.provisioningPhoneNumber'
			),
			localization.translate(
				'creator',
				'stepperOnboarding.steps.transitionProgress.callSummariesTransfers.finalizingAgent'
			),
			localization.translate(
				'creator',
				'stepperOnboarding.steps.transitionProgress.callSummariesTransfers.speakingSoon'
			)
		];
	}, [localization, stepKey, websiteUrl]);

	useEffect(() => {
		setActiveMessageIndex(0);

		if (messages.length < 2) {
			return undefined;
		}

		const messageInterval = globalThis.setInterval(() => {
			setActiveMessageIndex((currentIndex) =>
				Math.min(currentIndex + 1, messages.length - 1)
			);
		}, STEPPER_STEP_TRANSITION_PROGRESS_TIMEOUT_MS / messages.length);

		return () => {
			globalThis.clearInterval(messageInterval);
		};
	}, [messages]);

	const messageDurationMs =
		STEPPER_STEP_TRANSITION_PROGRESS_TIMEOUT_MS / messages.length;
	const typewriterStyle: StepperStepTransitionProgressTextStyle = {
		'--stepper-progress-message-characters':
			messages[activeMessageIndex].length,
		'--stepper-progress-message-duration': `${Math.min(
			1800,
			messageDurationMs - 200
		)}ms`
	};

	return (
		<Box
			animate={{ height: 48, opacity: 1 }}
			className="overflow-hidden"
			component={motion.div}
			data-testid="stepper-step-transition-progress"
			exit={{ height: 0, opacity: 0 }}
			initial={{ height: 0, opacity: 0 }}
			transition={{ duration: 0.28, ease: 'easeOut' }}
		>
			<Box className="relative h-12 overflow-hidden rounded-xl bg-white">
				<Box
					animate={{ width: `${STEPPER_PROGRESS_COMPLETE_PERCENT}%` }}
					aria-hidden="true"
					className="absolute inset-y-0 left-0 bg-[linear-gradient(90deg,rgba(255,255,255,1)_0%,rgba(182,220,255,1)_100%)]"
					component={motion.div}
					initial={{ width: '0%' }}
					transition={transition}
				/>
				<Box className="absolute inset-0 flex items-center px-5 py-3">
					<Text
						className="text-base font-semibold text-primary-900"
						data-testid="stepper-step-transition-progress-text"
					>
						<span
							className="stepper-step-transition-progress-typewriter"
							data-testid="stepper-step-transition-progress-typed-text"
							key={`${stepKey}-${activeMessageIndex}`}
							style={typewriterStyle}
						>
							{messages[activeMessageIndex]}
						</span>
					</Text>
				</Box>
			</Box>
		</Box>
	);
};

export default StepperStepTransitionProgressBar;
